import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import { toastRequestError } from '_utils/axios';

import type { AttendanceSearch, AttendanceStore, AttendanceUpdate } from "_types/requests/attendance";
import type { Attendance } from "_types/models/Attendance";

import { apiSwr, routes } from '_api';
import {
    currentAttendanceSession,
    findAttendance,
    finishAttendance,
    searchAttendances, startAttendanceSession, endAttendanceSession,
    storeAttendance,
    chatToken,
    videoAttendance
} from "_api/fetchers/attendances";

export function useAttendanceSearch(search: AttendanceSearch) {
    const queryKey = [...routes.attendances.search, search] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: searchAttendances,
    });

    const paginator = swr.data;
    const attendances = paginator?.data ?? [];
    const meta = paginator?.meta;

    return { ...swr, paginator, attendances, meta } as const;
}

export function useMyAttendanceSearch(search: AttendanceSearch) {
    const queryKey = [...routes.attendances.mySearch, search] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: searchAttendances,
    });

    const paginator = swr.data;
    const attendances = paginator?.data ?? [];
    const meta = paginator?.meta;

    return { ...swr, paginator, attendances, meta } as const;
}

export function useAttendance(id: Attendance['id']) {
    const queryKey = [...routes.attendance(id).show] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: findAttendance,
    });
    const attendance = swr.data;

    return { ...swr, attendance } as const;
}

export function useAttendanceStore() {
    const mutationKey = [...routes.attendances.store] as const;

    return useMutation({
        mutationKey,
        mutationFn: async (payload: AttendanceStore) => {
            return storeAttendance(mutationKey, payload);
        },

        onMutate: async d => {
            await apiSwr.cancelQueries({ queryKey: [...routes.attendances.search] });
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendances.search] });
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendance(d.id).show] });
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useFinishAttendance(id: Attendance['id']) {
    const mutationKey = [...routes.attendance(id).end] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return finishAttendance(mutationKey);
        },

        onMutate: async d => {

        },
        onSuccess: async d => {

        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useVideoAttendance(id: Attendance['id']) {
    const mutationKey = [...routes.attendance(id).video] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return videoAttendance(mutationKey);
        },

        onMutate: async d => {

        },
        onSuccess: async d => {

        },
        onError: err => {
            toastRequestError(err);
        },
    });
}



export function useCurrentAttendanceSession() {
    const queryKey = [...routes.attendances.currentSession] as const;

    const swr = useSuspenseQuery({
        queryKey,
        queryFn: currentAttendanceSession,
    });
    const attendanceSession = swr.data;

    return { ...swr, attendanceSession } as const;
}

export function useStartAttendanceSession() {
    const mutationKey = [...routes.attendances.startSession] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return startAttendanceSession(mutationKey);
        },
        onMutate: async d => {
            await apiSwr.cancelQueries({ queryKey: [...routes.attendances.currentSession] });
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendances.currentSession] });
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useEndAttendanceSession() {
    const mutationKey = [...routes.attendances.endSession] as const;

    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return endAttendanceSession(mutationKey);
        },
        onMutate: async d => {
            await apiSwr.cancelQueries({ queryKey: [...routes.attendances.currentSession] });
        },
        onSuccess: async d => {
            await apiSwr.invalidateQueries({ queryKey: [...routes.attendances.currentSession] });
        },
        onError: err => {
            toastRequestError(err);
        },
    });
}

export function useAttendanceChatToken(id: Attendance['id']) {
    const mutationKey = [...routes.attendance(id).chatToken] as const;
    return useMutation({
        mutationKey,
        mutationFn: async () => {
            return chatToken(mutationKey);
        },

        onMutate: async d => {

        },
        onSuccess: async d => {

        },
        onError: err => {
            toastRequestError(err);
        },
    });
}
